import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CalendarState {
  currentDate: Date;
  monthYear: string;
}

@Injectable({
  providedIn: 'root',
})
export class CalendarViewService {
  private readonly _currentState = new BehaviorSubject<CalendarState>({
    currentDate: new Date(),
    monthYear: this._getMonthYear(new Date()),
  });

  // Get current state value
  get currentState(): CalendarState {
    return this._currentState.value;
  }

  get currentState$(): Observable<CalendarState> {
    return this._currentState.asObservable();
  }

  // Update the current date
  updateDate(date: Date): void {
    const newState: CalendarState = {
      currentDate: new Date(date),
      monthYear: this._getMonthYear(date),
    };
    this._currentState.next(newState);
  }

  // Navigate by month delta
  navigateMonth(delta: number): void {
    const currentDate = this._currentState.value.currentDate;
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1);
    this.updateDate(newDate);
  }

  // Set specific month and year
  setMonthYear(month: number, year: number): void {
    const newDate = new Date(year, month - 1, 1);
    this.updateDate(newDate);
  }

  // Set only month (keep current year)
  setMonth(month: number): void {
    const currentState = this._currentState.value;
    const currentYear = currentState.currentDate.getFullYear();
    const newDate = new Date(currentYear, month - 1, 1);
    this.updateDate(newDate);
  }

  // Set only year (keep current month)
  setYear(year: number): void {
    const currentState = this._currentState.value;
    const currentMonth = currentState.currentDate.getMonth();
    const newDate = new Date(year, currentMonth, 1);
    this.updateDate(newDate);
  }

  // Initialize with specific date
  initialize(date?: Date, fixedMonth?: number, fixedYear?: number): void {
    let initialDate: Date;

    if (date) {
      initialDate = new Date(date);
    } else if (fixedMonth && fixedYear) {
      initialDate = new Date(fixedYear, fixedMonth - 1, 1);
    } else {
      initialDate = new Date();
    }

    this.updateDate(initialDate);
  }

  private _getMonthYear(date: Date): string {
    return `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
  }
}
